// src/components/maintenance/components/PendingView.js
import React, { useState } from 'react';
import { useRecordContext, useNotify, useRefresh } from 'react-admin';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Alert,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Upload as UploadIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { supabaseClient } from '../../supabase';
import { parseAndGeneratePreviewMultiple } from '../utils/csvParser';
import { identifyCSVType } from '../utils/csvGenerator';

const PendingView = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [uploading, setUploading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState({
    teachers: null,
    classes: null,
    students_parents: null,
  });

    const handleDownloadAll = async () => {
        try {
            setDownloadProgress(true);

            // Parse the rollback_file_path
            let rollbackPaths;
            try {
            rollbackPaths = JSON.parse(record.rollback_file_path);
            } catch (e) {
            throw new Error('This maintenance job was created with an older format. Please create a new job.');
            }

            // Download all three CSVs from storage
            const [teachersData, classesData, studentsData] = await Promise.all([
            supabaseClient.storage.from('maintenance-files').download(rollbackPaths.teachers),
            supabaseClient.storage.from('maintenance-files').download(rollbackPaths.classes),
            supabaseClient.storage.from('maintenance-files').download(rollbackPaths.students_parents),
            ]);

            // Check for errors
            if (teachersData.error) throw teachersData.error;
            if (classesData.error) throw classesData.error;
            if (studentsData.error) throw studentsData.error;

            // Get the blobs directly (don't convert to text and back)
            const teachersBlob = teachersData.data;
            const classesBlob = classesData.data;
            const studentsBlob = studentsData.data;

            // Create ZIP on the fly
            const JSZip = (await import('jszip')).default;
            const zip = new JSZip();
            
            // Add files as blobs to preserve encoding
            zip.file('teachers.csv', teachersBlob, { binary: true });
            zip.file('classes.csv', classesBlob, { binary: true });
            zip.file('students_parents.csv', studentsBlob, { binary: true });

            // Generate ZIP blob
            const zipBlob = await zip.generateAsync({ 
            type: 'blob',
            compression: 'DEFLATE',
            compressionOptions: {
                level: 6
            }
            });

            // Download the ZIP
            const url = window.URL.createObjectURL(zipBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `rollback_job${record.id}.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            notify('Rollback files downloaded successfully', { type: 'success' });
        } catch (error) {
            console.error('Download error:', error);
            notify(`Error downloading file: ${error.message}`, { type: 'error' });
        } finally {
            setDownloadProgress(false);
        }
    };

    const handleFileUpload = async (event) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) return;

        // Reset the file input
        event.target.value = null;

        try {
        setUploading(true);
        const newUploadedFiles = { ...uploadedFiles };

        // Process each file
        for (const file of files) {
            const text = await file.text();
            const csvType = identifyCSVType(text);

            // Upload to storage
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            const fileName = `${csvType}_job${record.id}_${timestamp}.csv`;
            const filePath = `${fileName}`;

            const { error: uploadError } = await supabaseClient.storage
            .from('maintenance-files')
            .upload(filePath, new Blob([text], { type: 'text/csv;charset=utf-8' }), {
                contentType: 'text/csv;charset=utf-8',
            });

            if (uploadError) throw uploadError;

            newUploadedFiles[csvType] = {
            name: file.name,
            path: filePath,
            uploaded: true,
            };
        }

      setUploadedFiles(newUploadedFiles);

      // Check if all three files are uploaded
      const allUploaded = Object.values(newUploadedFiles).every(f => f !== null);

      if (allUploaded) {
        // Parse all CSVs and generate preview
        notify('All files uploaded. Generating preview...', { type: 'info' });

        const previewData = await parseAndGeneratePreviewMultiple(
          newUploadedFiles,
          record
        );

        // Update maintenance operation
        const { error: updateError } = await supabaseClient
          .from('maintenance_operations')
          .update({
            uploaded_file_path: JSON.stringify(newUploadedFiles), // Store all paths as JSON
            status: 'uploaded',
            preview_data: previewData,
          })
          .eq('id', record.id);

        if (updateError) throw updateError;

        notify('Files uploaded successfully. Review changes below.', {
          type: 'success'
        });
        refresh();
      } else {
        const uploadedCount = Object.values(newUploadedFiles).filter(f => f).length;
        notify(`Uploaded ${uploadedCount} of 3 files. Please upload the remaining files.`, {
          type: 'info'
        });
      }

    } catch (error) {
      console.error('Upload error:', error);
      notify(`Error: ${error.message}`, { type: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const allFilesUploaded = Object.values(uploadedFiles).every(f => f !== null);

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Step 1: Download and Edit
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          Download the current database snapshot (ZIP file with 3 CSVs), edit them in Excel
          or any spreadsheet program, then upload all three edited files.
        </Alert>

        <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadAll}
            disabled={downloadProgress}
            fullWidth
          >
            {downloadProgress ? 'Downloading...' : 'Download All CSVs (ZIP)'}
          </Button>

          {downloadProgress && <LinearProgress />}

          <Divider sx={{ my: 2 }} />

          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              Important: You must upload all 3 CSV files
            </Typography>
            <Typography variant="body2" component="div">
              1. <strong>teachers.csv</strong> - Update teacher information<br />
              2. <strong>classes.csv</strong> - Update class information<br />
              3. <strong>students_parents.csv</strong> - Update student/parent information<br />
              <br />
              The system will automatically detect which file is which by inspecting the column headers.
            </Typography>
          </Alert>

          {/* Upload Status */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Upload Status:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    {uploadedFiles.teachers ?
                      <CheckCircleIcon color="success" /> :
                      <CancelIcon color="disabled" />
                    }
                  </ListItemIcon>
                  <ListItemText
                    primary="Teachers CSV"
                    secondary={uploadedFiles.teachers?.name || 'Not uploaded'}
                  />
                  {uploadedFiles.teachers && (
                    <Chip label="Uploaded" size="small" color="success" />
                  )}
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    {uploadedFiles.classes ?
                      <CheckCircleIcon color="success" /> :
                      <CancelIcon color="disabled" />
                    }
                  </ListItemIcon>
                  <ListItemText
                    primary="Classes CSV"
                    secondary={uploadedFiles.classes?.name || 'Not uploaded'}
                  />
                  {uploadedFiles.classes && (
                    <Chip label="Uploaded" size="small" color="success" />
                  )}
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    {uploadedFiles.students_parents ?
                      <CheckCircleIcon color="success" /> :
                      <CancelIcon color="disabled" />
                    }
                  </ListItemIcon>
                  <ListItemText
                    primary="Students & Parents CSV"
                    secondary={uploadedFiles.students_parents?.name || 'Not uploaded'}
                  />
                  {uploadedFiles.students_parents && (
                    <Chip label="Uploaded" size="small" color="success" />
                  )}
                </ListItem>
              </List>
            </CardContent>
          </Card>

          <Button
            variant="contained"
            component="label"
            startIcon={<UploadIcon />}
            disabled={uploading}
            fullWidth
            color="secondary"
          >
            {uploading ? 'Uploading and Processing...' : 'Upload CSV Files (Select All 3)'}
            <input
              type="file"
              hidden
              accept=".csv"
              multiple
              onChange={handleFileUpload}
            />
          </Button>

          {uploading && (
            <Box>
              <LinearProgress />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Processing files and generating preview...
              </Typography>
            </Box>
          )}

          {!allFilesUploaded && Object.values(uploadedFiles).some(f => f) && (
            <Alert severity="warning">
              You've uploaded {Object.values(uploadedFiles).filter(f => f).length} of 3 required files.
              Please upload the remaining files.
            </Alert>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default PendingView;